"use strict";
/**
 * Market category derivation for the filter chips — the additional-markets
 * modal's and the primary market picker's.
 *
 * Pure, DOM-free, venue-blind. Given the markets of one event, it
 * groups them into a small set of category chips so the operator can whittle a
 * ~220-market PM-US MLB event down to "just the Hits props" without scrolling.
 *
 * The grouping key is `marketCategory` — populated for PM-US from the
 * gateway's fine-grained `sportsMarketType` (e.g.
 * `baseball_player_hits`, `baseball_team_first_five_total`). Kalshi markets
 * leave it undefined: a Kalshi series is already homogeneous, so it yields <2
 * categories and the caller hides the chip row. That auto-hide is why this is
 * venue-blind — no `venue === "pm-us"` branch anywhere. Novig fills it from
 * `marketType` and names it itself (`marketCategoryLabel`).
 *
 * Two transforms make the raw enum presentable, both intentionally
 * zero-maintenance (no per-value curation, so new sports/props Just Work):
 *
 *   1. The nine per-inning winner variants (`..._inning1_winner` …
 *      `..._inning9_winner`) collapse to one `inning_winners` bucket — otherwise
 *      they'd be nine near-identical chips.
 *   2. Chip LABELS strip the sport prefix common to every value in the event
 *      (`baseball_`) and title-case the rest, so `baseball_player_hits` reads
 *      "Player Hits". The KEY keeps the prefix so it stays computable from a
 *      single market (see categoryKeyForMarket) without knowing the whole set.
 *
 * Chips are ordered by the COARSE market type (`marketCoarseType`) so game
 * lines lead and props trail — the fine `sportsMarketType` carries no display
 * rank of its own (its `sortOrder` is null; `event.sortType` is "price"). The
 * coarse tier is the one ordering signal the payload actually populates. Within
 * a tier, chips fall by count then label, floating the fullest buckets first.
 *
 * @module markets/market-categories
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.coarseRank = coarseRank;
exports.categoryKeyForMarket = categoryKeyForMarket;
exports.deriveMarketCategories = deriveMarketCategories;
/** Matches a per-inning winner variant so all nine collapse to one bucket. */
const INNING_WINNER_RE = /_inning\d+_winner$/;
const COLLAPSED_INNING_KEY = "inning_winners";
/** Coarse-type display order: game lines first, props last; unknown trails. */
const COARSE_TYPE_ORDER = ["moneyline", "spreads", "totals", "props"];
/** Rank of a coarse market type in COARSE_TYPE_ORDER (unknown → last). */
function coarseRank(coarseType) {
    const i = COARSE_TYPE_ORDER.indexOf((coarseType ?? "").toLowerCase());
    return i === -1 ? COARSE_TYPE_ORDER.length : i;
}
/** Tokens that read better upper-cased than title-cased in chip labels. */
const ACRONYMS = {
    rbis: "RBIs",
    rbi: "RBI",
    hr: "HR",
    era: "ERA",
    ml: "ML",
    f5: "F5",
};
/**
 * The category key for one market, or null if it carries no category (Kalshi,
 * or a PM-US market missing the field). Deterministic from the single market —
 * it must be, since the render path calls it per-row to apply the active chip.
 */
function categoryKeyForMarket(market) {
    const raw = market.marketCategory?.trim();
    if (!raw)
        return null;
    return INNING_WINNER_RE.test(raw) ? COLLAPSED_INNING_KEY : raw;
}
/** Title-case one snake_case segment run, upper-casing known acronyms. */
function titleCase(value) {
    return value
        .split("_")
        .filter(Boolean)
        .map((word) => {
        const lower = word.toLowerCase();
        if (ACRONYMS[lower])
            return ACRONYMS[lower];
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
        .join(" ");
}
/**
 * Longest `_`-boundary prefix shared by every key, never consuming a key
 * whole — the shortest key always keeps at least one trailing segment so a
 * label can never come out empty.
 */
function commonPrefix(keys) {
    if (keys.length < 2)
        return "";
    const split = keys.map((k) => k.split("_"));
    const maxSegments = Math.min(...split.map((s) => s.length)) - 1;
    let shared = 0;
    for (let i = 0; i < maxSegments; i++) {
        const seg = split[0][i];
        if (split.every((s) => s[i] === seg))
            shared++;
        else
            break;
    }
    return shared === 0 ? "" : split[0].slice(0, shared).join("_") + "_";
}
/**
 * Group markets into display categories, most-populated first. The collapsed
 * inning bucket is labelled "Inning Winners" explicitly, since its synthetic
 * key ("inning_winners") has no sport prefix to line up with the others.
 */
function deriveMarketCategories(markets) {
    const counts = new Map();
    // Best (lowest) coarse rank seen for each key — a fine category belongs to a
    // single coarse tier, but taking the min is robust if the payload disagrees.
    const ranks = new Map();
    const venueLabels = new Map();
    for (const market of markets) {
        const key = categoryKeyForMarket(market);
        if (!key)
            continue;
        counts.set(key, (counts.get(key) ?? 0) + 1);
        const rank = coarseRank(market.marketCoarseType);
        ranks.set(key, Math.min(ranks.get(key) ?? rank, rank));
        if (market.marketCategoryLabel)
            venueLabels.set(key, market.marketCategoryLabel);
    }
    const keys = [...counts.keys()];
    const prefix = commonPrefix(keys.filter((k) => k !== COLLAPSED_INNING_KEY));
    const categories = keys.map((key) => {
        let label;
        const venueLabel = venueLabels.get(key);
        if (venueLabel) {
            label = venueLabel;
        }
        else if (key === COLLAPSED_INNING_KEY) {
            label = "Inning Winners";
        }
        else {
            const stripped = prefix && key.startsWith(prefix) ? key.slice(prefix.length) : key;
            label = titleCase(stripped);
        }
        return { key, label, count: counts.get(key) ?? 0 };
    });
    // Coarse tier first (game lines → props), then fullest buckets, then label.
    return categories.sort((a, b) => (ranks.get(a.key) ?? 0) - (ranks.get(b.key) ?? 0) ||
        b.count - a.count ||
        a.label.localeCompare(b.label));
}
